import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Search as SearchIcon,
  BookOpen,
  FileText,
  MessageSquare,
  History,
  Users,
  Scroll,
  Sparkles,
  ChevronRight,
  ChevronLeft,
  Loader2,
} from 'lucide-react';
import { searchApi, tafsirApi } from '../services/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { GlassCard, ContentBadge } from '../components/shared';

const PAGE_SIZE = 10;

const SearchPage = () => {
  const { t } = useTranslation();
  const CATEGORIES = [
    { id: 'surahs',     label: t('search.cat_surahs'),     icon: BookOpen },
    { id: 'verses',     label: t('search.cat_verses'),     icon: FileText },
    { id: 'hadiths',    label: t('search.cat_hadiths'),    icon: MessageSquare },
    { id: 'events',     label: t('search.cat_events'),     icon: History },
    { id: 'companions', label: t('search.cat_companions'), icon: Users },
    { id: 'prophets',   label: t('search.cat_prophets'),   icon: Scroll },
  ];
  const ALL_IDS = CATEGORIES.map((c) => c.id);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQuery = searchParams.get('q') || '';
  const urlCategory = searchParams.get('category') || '';

  const [input, setInput] = useState(urlQuery);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState(ALL_IDS);
  const [tafsirResults, setTafsirResults] = useState(null);
  const [page, setPage] = useState(0);

  const isCategoryView = Boolean(urlCategory);
  const activeCategory = CATEGORIES.find((c) => c.id === urlCategory);

  // Run search when URL params change
  useEffect(() => {
    if (!urlQuery) {
      setResults(null);
      setTafsirResults(null);
      return;
    }
    setInput(urlQuery);
    let cancelled = false;
    setLoading(true);
    const types = isCategoryView ? urlCategory : selectedTypes.join(',');
    const limit = isCategoryView ? PAGE_SIZE : 10;
    const skip = isCategoryView ? page * PAGE_SIZE : 0;

    // Global search (6 standard categories)
    const mainPromise = isCategoryView && urlCategory === 'tafsir'
      ? Promise.resolve({ data: null })  // skip global search for tafsir-only view
      : searchApi.search(urlQuery, types, limit, skip);

    // Tafsir dedicated search (always fetched, can be filtered by category view)
    const tafsirLimit = isCategoryView && urlCategory === 'tafsir' ? PAGE_SIZE : 3;
    const tafsirSkip = isCategoryView && urlCategory === 'tafsir' ? page * PAGE_SIZE : 0;
    const tafsirPromise = (urlQuery.length >= 2)
      ? tafsirApi.search(urlQuery, { limit: tafsirLimit, skip: tafsirSkip })
      : Promise.resolve({ data: null });

    Promise.all([mainPromise, tafsirPromise])
      .then(([mainRes, tafRes]) => {
        if (cancelled) return;
        if (mainRes.data) setResults(mainRes.data);
        setTafsirResults(tafRes.data);
      })
      .catch(() => {
        if (!cancelled) { setResults(null); setTafsirResults(null); }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlQuery, urlCategory, page]);

  // Reset page when switching category
  useEffect(() => {
    setPage(0);
  }, [urlCategory, urlQuery]);

  const handleSearch = (e) => {
    e?.preventDefault();
    const q = input.trim();
    if (!q) return;
    // If in category view, clear category on new search (search everything)
    setSearchParams({ q });
  };

  const toggleType = (id) => {
    setSelectedTypes((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
    );
  };

  // Rerun search when filters change in global view
  useEffect(() => {
    if (isCategoryView || !urlQuery) return;
    let cancelled = false;
    setLoading(true);
    searchApi
      .search(urlQuery, selectedTypes.join(','), 10, 0)
      .then((res) => {
        if (!cancelled) setResults(res.data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTypes]);

  const counts = results?.counts || {};
  const totalAll = Object.values(counts).reduce((a, b) => a + b, 0);

  const renderItem = useCallback((cat, item, idx) => {
    switch (cat) {
      case 'surahs':
        return (
          <Link
            key={`surah-${item.number}`}
            to={`/quran/${item.number}`}
            data-testid={`result-surah-${item.number}`}
          >
            <GlassCard className="cursor-pointer hover:border-primary/30 transition-all">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <ContentBadge type="quran" className="mb-2" />
                  <h3 className="font-serif text-text-primary">
                    {item.number}. {item.name_french || item.name_english}
                  </h3>
                  <p className="font-arabic text-primary text-lg mt-1" dir="rtl">
                    {item.name_arabic}
                  </p>
                  <p className="text-xs text-text-muted mt-2">
                    {item.verses_count} versets · {item.revelation_place === 'Meccan' ? 'Mecquoise' : 'Médinoise'}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-text-muted flex-shrink-0" />
              </div>
            </GlassCard>
          </Link>
        );
      case 'verses':
        return (
          <motion.div
            key={`verse-${item.verse_key}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(idx * 0.02, 0.3) }}
          >
            <GlassCard
              className="cursor-pointer hover:border-primary/30 transition-all"
              onClick={() => navigate(`/quran/${item.surah_number}`)}
              data-testid={`result-verse-${item.verse_key}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <ContentBadge type="quran" />
                    <span className="text-xs text-text-muted">
                      {item.surah_number}:{item.verse_number}
                    </span>
                  </div>
                  <p className="font-arabic text-xl text-white text-right mb-2" dir="rtl">
                    {item.arabic_text}
                  </p>
                  <p className="text-sm text-text-secondary">
                    {item.translation_french || item.translation_english}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-text-muted flex-shrink-0" />
              </div>
            </GlassCard>
          </motion.div>
        );
      case 'hadiths':
        return (
          <GlassCard
            key={`hadith-${item.id}`}
            className="cursor-pointer hover:border-primary/30 transition-all"
            onClick={() => navigate(`/hadith/entry/${item.id}`)}
            data-testid={`search-result-hadith-${item.id}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <ContentBadge type="hadith" />
              <span className="text-xs text-text-muted">
                {item.collection_slug === 'bukhari' ? 'Sahih al-Boukhari' : 'Sahih Muslim'} · Livre {item.book_number} · #{item.id_in_book}
              </span>
            </div>
            {item.narrator_english && (
              <p className="text-xs italic text-text-muted mb-2">{item.narrator_english}</p>
            )}
            {item.preview_ar && (
              <p className="font-arabic text-lg text-text-primary text-right leading-loose line-clamp-2 mb-2" dir="rtl">
                {item.preview_ar}…
              </p>
            )}
            {item.preview_en && (
              <p className="text-sm text-text-secondary line-clamp-2">{item.preview_en}</p>
            )}
          </GlassCard>
        );
      case 'events':
        return (
          <GlassCard
            key={`event-${item.id}`}
            className="cursor-pointer hover:border-primary/30 transition-all"
            onClick={() => navigate(`/events/${item.id}`)}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <ContentBadge type="historical" className="mb-2" />
                <h3 className="font-serif text-text-primary">
                  {item.title_french || item.title_english}
                </h3>
                <p className="font-arabic text-primary text-sm" dir="rtl">{item.title_arabic}</p>
                <p className="text-sm text-text-secondary mt-2 line-clamp-2">{item.narrative}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-text-muted flex-shrink-0" />
            </div>
          </GlassCard>
        );
      case 'companions':
        return (
          <GlassCard
            key={`companion-${item.id}`}
            className="cursor-pointer hover:border-primary/30 transition-all"
            onClick={() => navigate(`/companions/${item.id}`)}
          >
            <h3 className="font-serif text-text-primary">
              {item.name_french || item.name_english}
            </h3>
            <p className="font-arabic text-primary" dir="rtl">{item.name_arabic}</p>
            {item.nickname && <p className="text-xs text-text-muted mt-1">{item.nickname}</p>}
          </GlassCard>
        );
      case 'prophets':
        return (
          <GlassCard
            key={`prophet-${item.id}`}
            className="cursor-pointer hover:border-primary/30 transition-all"
            onClick={() => navigate(`/prophets/${item.id}`)}
          >
            <h3 className="font-serif text-text-primary">
              {item.name_french || item.name_english}
            </h3>
            <p className="font-arabic text-primary" dir="rtl">{item.name_arabic}</p>
            <p className="text-sm text-text-secondary mt-2 line-clamp-2">{item.summary}</p>
          </GlassCard>
        );
      default:
        return null;
    }
  }, [navigate]);

  const renderTafsirSection = () => {
    const total = tafsirResults?.total || 0;
    if (total === 0) return null;
    const items = tafsirResults.items || [];
    return (
      <section data-testid="section-tafsir">
        <div className="flex items-center justify-between mb-4">
          <h2 className="flex items-center gap-2 text-lg font-serif text-text-primary">
            <Sparkles className="w-5 h-5 text-primary" />
            Tafsir ({total})
          </h2>
          {total > items.length && (
            <button
              onClick={() => setSearchParams({ q: urlQuery, category: 'tafsir' })}
              className="text-sm text-primary hover:underline"
              data-testid="see-all-tafsir"
            >
              {t('search.see_all', { total })}
            </button>
          )}
        </div>
        <div className="space-y-3">
          {items.map((t_) => (
            <Link
              key={t_.group_id}
              to={`/quran/${t_.group.from_surah}`}
              className="block"
              data-testid={`tafsir-result-${t_.group_id}`}
            >
              <GlassCard className="hover:border-primary/30 transition-all">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-2 py-0.5 rounded bg-primary/15 text-primary">
                      {t_.source.display_name_localized?.fr || t_.source.display_name}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-text-muted">
                      {t_.language_code}
                    </span>
                  </div>
                  <span className="text-xs text-text-muted">
                    {t_.group.from_surah}:{t_.group.from_ayah}
                    {t_.group.span_size > 1 &&
                      ` → ${t_.group.to_surah}:${t_.group.to_ayah}`}
                  </span>
                </div>
                <p
                  className={`text-sm text-text-secondary leading-relaxed ${
                    t_.language_code === 'ar' ? 'text-right font-arabic text-base' : ''
                  }`}
                  dir={t_.language_code === 'ar' ? 'rtl' : 'ltr'}
                >
                  {t_.snippet}
                </p>
              </GlassCard>
            </Link>
          ))}
        </div>
      </section>
    );
  };

  const renderCategorySection = (cat) => {
    const total = counts[cat.id] || 0;
    const items = results?.[cat.id] || [];
    if (total === 0) return null;
    const gridCols = cat.id === 'companions' || cat.id === 'prophets' || cat.id === 'surahs'
      ? 'grid grid-cols-1 sm:grid-cols-2 gap-3'
      : 'space-y-3';
    return (
      <section key={cat.id} data-testid={`section-${cat.id}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="flex items-center gap-2 text-lg font-serif text-text-primary">
            <cat.icon className="w-5 h-5 text-primary" />
            {cat.label} ({total})
          </h2>
          {total > items.length && (
            <button
              onClick={() =>
                setSearchParams({ q: urlQuery, category: cat.id })
              }
              className="text-sm text-primary hover:underline"
              data-testid={`see-all-${cat.id}`}
            >
              {t('search.see_all', { total })}
            </button>
          )}
        </div>
        <div className={gridCols}>
          {items.map((item, idx) => renderItem(cat.id, item, idx))}
        </div>
      </section>
    );
  };

  // Category view
  if (isCategoryView) {
    // Dedicated tafsir category view
    if (urlCategory === 'tafsir') {
      const total = tafsirResults?.total || 0;
      const items = tafsirResults?.items || [];
      const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
      return (
        <div className="min-h-screen py-12 px-4">
          <div className="max-w-4xl mx-auto">
            <button
              onClick={() => setSearchParams({ q: urlQuery })}
              className="inline-flex items-center text-text-muted hover:text-primary mb-6 transition-colors"
              data-testid="back-to-all-results"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              {t('search.back_to_all')}
            </button>
            <div className="mb-8">
              <span className="overline mb-2 block">{t('search.filtered_results')}</span>
              <h1 className="text-3xl sm:text-4xl font-serif text-text-primary mb-2 flex items-center gap-3">
                <Sparkles className="w-7 h-7 text-primary" />
                {t('search.cat_tafsir')}
              </h1>
              <p className="text-text-secondary">
                {t('search.category_count', { count: total, q: urlQuery })}
              </p>
            </div>

            {loading && (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            )}

            {!loading && items.length > 0 && (
              <>
                <div className="space-y-3" data-testid="category-results">
                  {items.map((t_) => (
                    <Link key={t_.group_id} to={`/quran/${t_.group.from_surah}`} className="block">
                      <GlassCard className="hover:border-primary/30 transition-all">
                        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs px-2 py-0.5 rounded bg-primary/15 text-primary">
                              {t_.source.display_name_localized?.fr || t_.source.display_name}
                            </span>
                            <span className="text-[10px] uppercase tracking-wider text-text-muted">
                              {t_.language_code}
                            </span>
                          </div>
                          <span className="text-xs text-text-muted">
                            {t_.group.from_surah}:{t_.group.from_ayah}
                            {t_.group.span_size > 1 &&
                              ` → ${t_.group.to_surah}:${t_.group.to_ayah}`}
                          </span>
                        </div>
                        <p
                          className={`text-sm text-text-secondary leading-relaxed ${
                            t_.language_code === 'ar' ? 'text-right font-arabic text-base' : ''
                          }`}
                          dir={t_.language_code === 'ar' ? 'rtl' : 'ltr'}
                        >
                          {t_.snippet}
                        </p>
                      </GlassCard>
                    </Link>
                  ))}
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-3 mt-10">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 0}
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      className="border-border"
                      data-testid="pagination-prev"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      {t('common.previous')}
                    </Button>
                    <span className="text-sm text-text-muted">
                      {t('common.page_of', { current: page + 1, total: totalPages })}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= totalPages - 1}
                      onClick={() => setPage((p) => p + 1)}
                      className="border-border"
                      data-testid="pagination-next"
                    >
                      {t('common.next')}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      );
    }

    const total = counts[urlCategory] || 0;
    const items = results?.[urlCategory] || [];
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const gridCols = urlCategory === 'companions' || urlCategory === 'prophets' || urlCategory === 'surahs'
      ? 'grid grid-cols-1 sm:grid-cols-2 gap-3'
      : 'space-y-3';

    return (
      <div className="min-h-screen py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => setSearchParams({ q: urlQuery })}
            className="inline-flex items-center text-text-muted hover:text-primary mb-6 transition-colors"
            data-testid="back-to-all-results"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {t('search.back_to_all')}
          </button>

          <div className="mb-8">
            <span className="overline mb-2 block">{t('search.filtered_results')}</span>
            <h1 className="text-3xl sm:text-4xl font-serif text-text-primary mb-2 flex items-center gap-3">
              {activeCategory?.icon && (
                <activeCategory.icon className="w-7 h-7 text-primary" />
              )}
              {activeCategory?.label}
            </h1>
            <p className="text-text-secondary">
              {t('search.category_count', { count: total, q: urlQuery })}
            </p>
          </div>

          {loading && (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          )}

          {!loading && items.length > 0 && (
            <>
              <div className={gridCols} data-testid="category-results">
                {items.map((item, idx) => renderItem(urlCategory, item, idx))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 mt-10">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    className="border-border"
                    data-testid="pagination-prev"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    {t('common.previous')}
                  </Button>
                  <span className="text-sm text-text-muted">
                    {t('common.page_of', { current: page + 1, total: totalPages })}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage((p) => p + 1)}
                    className="border-border"
                    data-testid="pagination-next"
                  >
                    {t('common.next')}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </>
          )}

          {!loading && items.length === 0 && (
            <div className="text-center py-16">
              <SearchIcon className="w-16 h-16 text-text-muted mx-auto mb-4" />
              <h3 className="text-lg font-medium text-text-primary mb-2">
                {t('search.no_results_simple')}
              </h3>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Default view (all categories)
  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <span className="overline mb-4 block">{t('search.overline')}</span>
          <h1 className="text-3xl sm:text-4xl font-serif text-text-primary mb-4">
            {t('search.title')}
          </h1>
          <p className="text-text-secondary max-w-xl mx-auto">
            {t('search.subtitle')}
          </p>
        </div>

        <form onSubmit={handleSearch} className="mb-8">
          <div className="relative">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <Input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('search.input_placeholder')}
              className="pl-12 pr-24 py-6 text-lg bg-surface border-border"
              data-testid="search-input"
            />
            <Button
              type="submit"
              disabled={loading || !input.trim()}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-primary text-background hover:bg-primary-hover"
              data-testid="search-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('search.submit')}
            </Button>
          </div>
        </form>

        <div className="flex flex-wrap gap-4 justify-center mb-8">
          {CATEGORIES.map((category) => (
            <label key={category.id} className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={selectedTypes.includes(category.id)}
                onCheckedChange={() => toggleType(category.id)}
                className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
              <category.icon className="w-4 h-4 text-text-muted" />
              <span className="text-sm text-text-secondary">{category.label}</span>
            </label>
          ))}
        </div>

        {results && (
          <div className="space-y-8">
            <p className="text-text-muted text-sm">
              {t('search.results_count', { total: totalAll, q: urlQuery })}
            </p>

            {renderTafsirSection()}

            {CATEGORIES.map(renderCategorySection)}

            {totalAll === 0 && (
              <div className="text-center py-16">
                <SearchIcon className="w-16 h-16 text-text-muted mx-auto mb-4" />
                <h3 className="text-lg font-medium text-text-primary mb-2">
                  {t('search.no_results_title')}
                </h3>
                <p className="text-text-secondary">
                  {t('search.no_results_desc')}
                </p>
              </div>
            )}
          </div>
        )}

        {!results && !loading && !urlQuery && (
          <div className="text-center py-16">
            <SearchIcon className="w-16 h-16 text-text-muted mx-auto mb-4" />
            <h3 className="text-lg font-medium text-text-primary mb-2">
              {t('search.start_title')}
            </h3>
            <p className="text-text-secondary">
              {t('search.start_desc')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
